import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

type SignMode = 'both' | 'positive' | 'negative';

interface NumericInputProps {
  value: number | null;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  integer?: boolean;
  sign?: SignMode;
  step?: number;
  placeholder?: string;
  className?: string;
  id?: string;
  disabled?: boolean;
  onBlur?: () => void;
}

const NumericInput = React.forwardRef<HTMLInputElement, NumericInputProps>(
  (
    {
      value,
      onChange,
      min,
      max,
      integer = true,
      sign = 'both',
      step,
      placeholder,
      className,
      id,
      disabled,
      onBlur: onBlurProp,
    },
    ref,
  ) => {
    const formatValue = (val: number | null): string => {
      if (val === null) return '';
      return integer ? String(val) : val.toFixed(2);
    };

    const [displayValue, setDisplayValue] = useState<string>(formatValue(value));
    const isFocusedRef = useRef(false);

    useEffect(() => {
      if (isFocusedRef.current) return;
      setDisplayValue(formatValue(value));
    }, [value, integer]);

    const clamp = useCallback(
      (num: number): number => {
        if (min !== undefined && num < min) return min;
        if (max !== undefined && num > max) return max;
        return num;
      },
      [min, max],
    );

    const isAllowedSign = useCallback(
      (num: number): boolean => {
        if (sign === 'positive') return num >= 0;
        if (sign === 'negative') return num <= 0;
        return true;
      },
      [sign],
    );

    const parse = useCallback((raw: string): number => (integer ? parseInt(raw, 10) : parseFloat(raw)), [integer]);

    const isIntermediateState = (raw: string): boolean => {
      if (raw === '' || raw === '-') return true;
      if (!integer && (raw.endsWith('.') || raw === '-.')) return true;
      return false;
    };

    const buildPattern = (): RegExp => {
      const negativeAllowed = sign !== 'positive';
      const signPart = negativeAllowed ? '-?' : '';

      if (integer) {
        return new RegExp(`^${signPart}\\d*$`);
      }

      return new RegExp(`^${signPart}\\d*\\.?\\d*$`);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (isIntermediateState(raw)) {
        if (raw === '-' && sign === 'positive') return;
        setDisplayValue(raw);
        return;
      }

      const pattern = buildPattern();
      if (!pattern.test(raw)) return;

      setDisplayValue(raw);
      const parsed = parse(raw);
      if (!isNaN(parsed) && isAllowedSign(parsed)) {
        onChange(clamp(parsed));
      }
    };

    const handleFocus = () => {
      isFocusedRef.current = true;
    };

    const handleBlur = () => {
      isFocusedRef.current = false;
      if (displayValue === '' || displayValue === '-' || displayValue === '-.') {
        setDisplayValue('');
        onChange(null);
        onBlurProp?.();
        return;
      }

      const parsed = parse(displayValue);
      if (isNaN(parsed) || !isAllowedSign(parsed)) {
        setDisplayValue('');
        onChange(null);
        onBlurProp?.();
        return;
      }

      const finalValue = clamp(parsed);
      setDisplayValue(formatValue(finalValue));
      onChange(finalValue);
      onBlurProp?.();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleBlur();
        return;
      }

      if (step !== undefined && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const current = value ?? min ?? 0;
        const next = e.key === 'ArrowUp' ? current + step : current - step;
        const clamped = clamp(next);
        if (isAllowedSign(clamped)) {
          onChange(clamped);
        }
      }
    };

    const inputMode = integer ? 'numeric' : 'decimal';

    return (
      <input
        ref={ref}
        id={id}
        type="text"
        inputMode={inputMode}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input flex h-9 w-full min-w-0 rounded-md border bg-background px-3 py-1 text-base transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
          className,
        )}
      />
    );
  },
);

NumericInput.displayName = 'NumericInput';

export type { NumericInputProps, SignMode };
export { NumericInput };
